<script setup lang="ts">
// Vue
import { computed } from "vue";
import { Trash2 } from "@lucide/vue";

// Components
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";

// Types
import { ReferenceAreaTypeOptionType } from "@/dataProviders/ReferenceAreaTypeOptions";

const props = defineProps<{
  referenceAreaTypeOptions: ReferenceAreaTypeOptionType[];
  item: { id: string; key: string; value: number | null; description: string | null };
  wasValidated: boolean;
}>();

const emits = defineEmits<{
  handleRemoveReferenceArea: [id: string];
}>();

const currentOption = computed<ReferenceAreaTypeOptionType | null>(
  () => props.referenceAreaTypeOptions.find((type) => type.value === props.item.key) ?? null
);

const totalReferenceArea = computed(() => {
  if (!currentOption.value) return 0;
  if (!props.item.value) return 0;
  return props.item.value * currentOption.value.multiplier;
});

const selectedOptionFullText = computed(() => currentOption.value?.label ?? "");

const areaTypeOptions = computed(() => props.referenceAreaTypeOptions.map((type) => ({ name: type.label, value: type.value })));

const handleSelectAreaType = (value: string) => {
  props.item.key = value;
};
</script>

<template>
  <!-- Column spans are container-tiered: the wizard rails make viewport breakpoints a bad proxy
       for this row's real width, and the Description placeholder needs ~106px to render unclipped. -->
  <div
    class="rounded-8 bg-slate-0 @container grid w-full grid-cols-12 items-start justify-between gap-4 border border-slate-200 p-4">
    <SelectInputWithKeyValue
      v-tooltip="{ text: selectedOptionFullText, disabled: !selectedOptionFullText }"
      :value="item.key"
      :options="areaTypeOptions"
      class="col-span-12 @xl:col-span-4 @3xl:col-span-5"
      label="Building structure"
      placeholder="Select"
      required
      :error-msg="wasValidated && currentOption === null ? 'Select a structure' : ''"
      @update:value="handleSelectAreaType" />

    <TextInputNew
      v-model="item.description"
      class="col-span-12 @xl:col-span-3 @3xl:col-span-2"
      label="Description"
      placeholder="Optional" />

    <NumberInput
      v-model:value="item.value"
      class="col-span-12 @xl:col-span-2"
      color="primary"
      label="Area (m&#178;)"
      :positive="true"
      :align-right="true"
      data-cy="area"
      :error-msg="wasValidated && (item.value === null || item.value < 0) ? 'Enter the area' : ''"
      :required="true" />

    <!-- No :key remount hacks here: NumberInput watches its value prop, and a dynamic key on these
         siblings made Vue duplicate the node when the structure select toggled the spacer below. -->
    <NumberInput
      v-show="currentOption?.multiplier"
      class="col-span-12 @xl:col-span-2"
      :align-right="true"
      :value="totalReferenceArea"
      color="primary"
      label="Counts as (m&#178;)"
      tooltip="The entered area times this structure type's factor. This is the amount added to the project's total reference area."
      :disabled="true" />

    <div
      v-if="!currentOption?.multiplier"
      class="col-span-12 @xl:col-span-2"></div>

    <div class="col-span-12 flex justify-end self-end @xl:col-span-1">
      <button
        type="button"
        aria-label="Remove reference area"
        data-cy="delete-reference-area-btn"
        class="focus-ring interactive-active duration-fast ease-standard rounded-4 p-1 transition-colors hover:bg-slate-100"
        @click="() => emits('handleRemoveReferenceArea', item.id)">
        <Trash2
          class="icon-sm text-app-red-600"
          :stroke-width="1.5" />
      </button>
    </div>
  </div>
</template>
